import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import inventoryApi from '../../../services/inventoryApi';
import productApi from '../../../services/productApi';
import productionApi from '../../../services/productionApi';
import realtime from '../../../services/realtime';
import { useFilter } from '../../../contexts/FilterContext';
import ProductTable from './ProductTable';
import CsvImportExport from './bulk/CsvImportExport';
import ProductionAnalyticsTab from './ProductionAnalyticsTab';
import PageLoader from '../../../components/common/PageLoader';
import CreateProductModal from '../../modals/CreateProductModal';
import './ProductionDashboard.css';

function ProductionDashboard() {
  const queryClient = useQueryClient();
  const { selectedProductIds } = useFilter();
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState('products');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  const [filters, setFilters] = useState({
    skip: 0,
    limit: 50,
    active_only: false,
    flavor: '',
    recipe: true,
    product_ids: [],
    sort: 'flavor:asc',
  });

  useEffect(() => {
    setFilters((prev) => {
      if (JSON.stringify(prev.product_ids) === JSON.stringify(selectedProductIds)) return prev;
      return { ...prev, product_ids: selectedProductIds, skip: 0 };
    });
    setCurrentPage(1);
  }, [selectedProductIds]);

  const { data: productsData, isLoading, isError, error } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const { data: plans } = useQuery({
    queryKey: ['production-plans'],
    queryFn: () => productionApi.listPlans({ limit: 50 }),
    staleTime: 30000,
  });

  const { data: capacity } = useQuery({
    queryKey: ['capacity'],
    queryFn: () => inventoryApi.productionCapacity({ scope: 'overall' }),
    refetchInterval: 45000,
    staleTime: 40000,
  });

  const invalidateProductData = useCallback(() => {
    ['products', 'production-capacity', 'inventory-alerts', 'capacity'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key], refetchType: 'active' })
    );
  }, [queryClient]);

  const updateItemMutation = useMutation({
    mutationFn: ({ id, payload, meta }) => productApi.update(id, payload, meta),
    onMutate: async ({ id, payload }) => {
      setUnsaved(true);
      await queryClient.cancelQueries({ queryKey: ['products'] });
      const prev = queryClient.getQueryData(['products', filters]);
      queryClient.setQueryData(['products', filters], {
        ...prev,
        items: prev?.items?.map((it) => (it.id === id ? { ...it, ...payload, _saving: true } : it)),
      });
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['products', filters], ctx.prev);
      showError(`Update failed: ${err.response?.data?.message || err.message}`);
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(['products', filters], (old) =>
        old ? { ...old, items: old.items.map((it) => (it.id === id ? { ...data } : it)) } : old
      );
      showSuccess(`"${data.flavor || id}" updated`);
    },
    onSettled: () => {
      setUnsaved(false);
      invalidateProductData();
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id) => productApi.remove(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['products'] });
      const prev = queryClient.getQueryData(['products', filters]);
      queryClient.setQueryData(['products', filters], {
        ...prev,
        items: prev?.items?.filter((it) => it.id !== id),
        total: (prev?.total || 0) - 1,
      });
      return { prev };
    },
    onError: (err, id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['products', filters], ctx.prev);
      showError(`Delete failed: ${err.response?.data?.message || err.message}`);
    },
    onSuccess: (_data, id) => {
      const product = productsData?.items?.find((p) => p.id === id);
      showSuccess(`"${product?.flavor || id}" deleted`);
    },
    onSettled: invalidateProductData,
  });

  useEffect(() => {
    let timer;
    const debouncedInvalidate = () => {
      clearTimeout(timer);
      timer = setTimeout(invalidateProductData, 500);
    };
    const unsub1 = realtime.subscribe('products.update', debouncedInvalidate);
    const unsub2 = realtime.subscribe('inventory.alert', () =>
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] })
    );
    const unsub3 = realtime.subscribe('production.plan.update', () =>
      queryClient.invalidateQueries({ queryKey: ['production-plans'] })
    );
    return () => {
      clearTimeout(timer);
      unsub1?.();
      unsub2?.();
      unsub3?.();
    };
  }, [queryClient, invalidateProductData]);

  useEffect(() => {
    const handler = (e) => { if (unsaved) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unsaved]);

  const onRowChange = useCallback(
    async (id, changes, meta) => {
      const prevItem = productsData?.items?.find((x) => x.id === id);
      if (prevItem) {
        undoStack.current.push({ id, prev: prevItem, next: { ...prevItem, ...changes } });
        redoStack.current = [];
      }
      try {
        await updateItemMutation.mutateAsync({ id, payload: changes, meta });
        return true;
      } catch {
        return false;
      }
    },
    [updateItemMutation, productsData?.items]
  );

  const onDeleteProduct = useCallback(
    async (id) => {
      try { await deleteProductMutation.mutateAsync(id); return true; }
      catch { return false; }
    },
    [deleteProductMutation]
  );

  const products = useMemo(() => productsData?.items || [], [productsData]);
  const total = productsData?.total || 0;

  const [sortField, sortDirection] = useMemo(() => {
    const [f, d] = (filters.sort || 'flavor:asc').split(':');
    return [f, d];
  }, [filters.sort]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, skip: 0 }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      setFilters((prev) => ({ ...prev, skip: (page - 1) * filters.limit }));
      setCurrentPage(page);
    },
    [filters.limit]
  );

  const handleSort = useCallback((field, direction) => {
    setFilters((prev) => ({ ...prev, sort: `${field}:${direction}` }));
  }, []);

  const scheduledPlans = plans?.items?.length ?? (Array.isArray(plans) ? plans.length : 0);
  const avgMargin = products.length > 0
    ? products.reduce((sum, p) => sum + (p.wholesale_margin || 0), 0) / products.length
    : 0;
  const activeCount = products.filter((p) => p.is_active).length;

  if (isLoading && !productsData) {
    return <PageLoader title="Loading product data..." message="" />;
  }

  return (
    <div className="production-dashboard">
      <div className="pd-header">
        <h1 className="pd-title">Production Dashboard</h1>
        <div className="pd-header-actions">
          <div className="pd-csv-wrap">
            <CsvImportExport onImported={() => queryClient.invalidateQueries({ queryKey: ['products'] })} />
          </div>
          <button className="pd-btn-primary" onClick={() => setIsCreateModalOpen(true)}>
            + New Product
          </button>
        </div>
      </div>

      <div className="pd-kpi-strip">
        <div className="pd-kpi-card">
          <span className="pd-kpi-label">Total Products</span>
          <span className="pd-kpi-value">{total}</span>
        </div>
        <div className="pd-kpi-card">
          <span className="pd-kpi-label">Active (this page)</span>
          <span className="pd-kpi-value success">{activeCount}</span>
        </div>
        <div className="pd-kpi-card">
          <span className="pd-kpi-label">Scheduled Plans</span>
          <span className="pd-kpi-value">{scheduledPlans}</span>
        </div>
        <div className="pd-kpi-card">
          <span className="pd-kpi-label">Avg Wholesale Margin</span>
          <span className={`pd-kpi-value ${avgMargin >= 0.3 ? 'success' : avgMargin >= 0.15 ? 'warn' : 'danger'}`}>
            {(avgMargin * 100).toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="pd-tabs">
        <button className={`pd-tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
          Products
        </button>
        <button className={`pd-tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
          Analytics
        </button>
      </div>

      {activeTab === 'products' && (
        <ProductTable
          products={products}
          total={total}
          onRowChange={onRowChange}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          filters={filters}
          currentPage={currentPage}
          pageSize={filters.limit}
          sortField={sortField}
          sortDirection={sortDirection}
          isLoading={isLoading}
          error={isError ? error : null}
          onDeleteProduct={onDeleteProduct}
        />
      )}

      {activeTab === 'analytics' && (
        <ProductionAnalyticsTab capacity={capacity} products={products} />
      )}

      <CreateProductModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}

export default ProductionDashboard;
